import moment, { Moment, unitOfTime } from 'moment';
import { isEmpty } from './validation.util';

export const DEFAULT_SERVER_DATE_FORMAT = 'YYYY-MM-DD';

export const DEFAULT_SERVER_DATE_HOUR_FORMAT = 'YYYY-MM-DD hh:mm:ss';

export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

const DEFAULT_DATE_FORMAT_REGEX = /([0-2][0-9]|3[0-1])\/(0[0-9]|1[0-2])\/[0-9]{4}/;

const DEFAULT_DATE_FORMAT_SERVER_REGEX = /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;

const DEFAULT_DATE_HOUR_FORMAT_REGEX = /^([1-9]|([012][0-9])|(3[01]))\/([0]{0,1}[1-9]|1[012])\/\d\d\d\d (20|21|22|23|[0-1]?\d):[0-5]?\d:[0-5]?\d$/;

export const format = (date: Date | Moment, pattern: string): string =>
  moment(date).locale('pt-br').format(pattern);

export const toDefaultServerDateFormat = (
  date: Date | Moment | null,
): string => {
  let result = '';
  if (date) {
    result = format(date, DEFAULT_SERVER_DATE_FORMAT);
  }
  return result;
};

export const startOf = (date: Date, type: unitOfTime.StartOf): Date =>
  moment(date).clone().startOf(type).toDate();

export const endOf = (date: Date, type: unitOfTime.StartOf): Date =>
  moment(date).clone().endOf(type).toDate();

export const firstDayCurrentMonthStr = (): string =>
  toDefaultServerDateFormat(startOf(moment().toDate(), 'month'));

export const lastDayCurrentMonthStr = (): string =>
  toDefaultServerDateFormat(endOf(moment().toDate(), 'month'));

export const dateHourToString = (date: Date): string =>
  format(date, DEFAULT_SERVER_DATE_HOUR_FORMAT);

export const firstDayHourCurrentMonthStr = (): string =>
  dateHourToString(moment().clone().startOf('month').toDate());

export const lastDayHourCurrentMonthStr = (): string =>
  dateHourToString(moment().clone().endOf('month').toDate());

export const toDefaultDateFormat = (date: Date): string =>
  format(date, DEFAULT_DATE_FORMAT);

export const isAfter = (firstDate: Date, lastDate: Date): boolean =>
  moment(firstDate).isAfter(lastDate);

export const isBefore = (firstDate: Date, lastDate: Date): boolean =>
  moment(firstDate).isBefore(lastDate);

export const subtract = (
  date: Date,
  quantity: number,
  type: unitOfTime.DurationConstructor,
): Date => moment(date).subtract(quantity, type).toDate();

export const add = (
  date: Date,
  quantity: number,
  type: unitOfTime.DurationConstructor,
): Date => moment(date).add(quantity, type).toDate();

export const isTomorrow = (date: Date | undefined): boolean => {
  const tomorrow = moment().clone().add(1, 'day');
  return date !== undefined && tomorrow.isSame(date, 'day');
};
export const isSameOrAfter = (firstDate: Date, secondDate: Date): boolean => {
  return moment(firstDate).isSameOrAfter(secondDate, 'day');
};

export const isValid = (date: Date): boolean => moment(date).isValid();

export const toServerDateFormat = (dateStr: string): string => {
  let result = dateStr;
  if (DEFAULT_DATE_FORMAT_REGEX.test(dateStr)) {
    const day = Number(dateStr.slice(0, 2));
    const month = Number(dateStr.slice(3, 5));
    const year = Number(dateStr.slice(6));
    result = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
      2,
      '0',
    )}`;
  }
  return result;
};

export const capitalizeWord = (value: string): string => {
  let result = '';
  if (typeof value === 'string') {
    result = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
  return result;
};

export const truncate = (value: string, truncateLength: number): string => {
  let slicedText = '';

  if (value) {
    if (value?.length <= truncateLength) {
      slicedText = value;
    } else {
      slicedText = `${value.slice(0, truncateLength)}...`;
    }
  }
  return slicedText;
};

export const toDate = (dateStr: string): Date | undefined => {
  let result;
  if (!isEmpty(dateStr)) {
    if (DEFAULT_DATE_HOUR_FORMAT_REGEX.test(dateStr)) {
      const day = Number(dateStr.slice(0, 2));
      const month = Number(dateStr.slice(3, 5));
      const year = Number(dateStr.slice(6, 10));
      const hour = Number(dateStr.slice(11, 13));
      const minute = Number(dateStr.slice(14, 16));
      const second = Number(dateStr.slice(17, 19));
      result = new Date(year, month - 1, day, hour, minute, second);
    } else if (DEFAULT_DATE_FORMAT_REGEX.test(dateStr)) {
      const day = Number(dateStr.slice(0, 2));
      const month = Number(dateStr.slice(3, 5));
      const year = Number(dateStr.slice(6));
      result = new Date(year, month - 1, day);
    } else if (DEFAULT_DATE_FORMAT_SERVER_REGEX.test(dateStr)) {
      const year = Number(dateStr.slice(0, 4));
      const month = Number(dateStr.slice(5, 7));
      const day = Number(dateStr.slice(8));
      result = new Date(year, month - 1, day);
    }
  }
  return result;
};

export const isAfterStr = (
  dateStr: string,
  dateToCompareStr: string,
): boolean => {
  let result = false;
  if (!isEmpty(dateStr) && !isEmpty(dateToCompareStr)) {
    const dateFmt = toDate(dateStr);
    const dateToCompareFmt = toDate(dateStr);

    if (dateFmt && dateToCompareFmt) {
      result = isAfter(dateFmt, dateToCompareFmt);
    }
  }
  return result;
};

export const isBeforeStr = (
  dateStr: string,
  dateToCompareStr: string,
): boolean => {
  let result = false;
  if (!isEmpty(dateStr) && !isEmpty(dateToCompareStr)) {
    const dateFmt = toDate(dateStr);
    const dateToCompareFmt = toDate(dateStr);

    if (dateFmt && dateToCompareFmt) {
      result = isBefore(dateFmt, dateToCompareFmt);
    }
  }
  return result;
};
